import { h, clear, toast, confirmModal } from '../utils/dom.js';
import { iconSvg } from '../utils/icons.js';
import { Token, CurrentUser } from '../services/api.js';
import { AuthAPI } from '../services/endpoints.js';
import { navigate } from '../utils/router.js';
import { LayoutDashboard, Building2, ScrollText, Puzzle, Users, ClipboardCheck, LogOut, Video } from 'lucide';

const roleLabel = (r) => r === 'admin' ? 'administrador' : r === 'instructor' ? 'instructor' : 'beneficiario';

const closeSidebar = () => {
  const sidebar = document.getElementById('sidebar');
  const backdrop = document.getElementById('sidebarBackdrop');
  sidebar.classList.remove('open');
  if (backdrop) backdrop.classList.remove('visible');
};

const ICON_MAP = {
  'Panel': LayoutDashboard,
  'Sedes': Building2,
  'Auditoría': ScrollText,
  'Servicios': Puzzle,
  'Usuarios': Users,
  'Solicitudes': ClipboardCheck,
  'Sesiones': Video,
};

export const renderSidebar = (activeRoute) => {
  const sidebar = document.getElementById('sidebar');
  const nav = document.getElementById('nav');
  const footer = sidebar.querySelector('.sidebar-footer');
  const toggleBtn = document.getElementById('sidebarToggle');
  const backdrop = document.getElementById('sidebarBackdrop');

  const user = CurrentUser.get();
  if (!Token.get() || !user) {
    sidebar.classList.add('hidden');
    if (backdrop) backdrop.classList.remove('visible');
    return;
  }
  sidebar.classList.remove('hidden');
  closeSidebar();

  clear(footer);

  const userName = h('strong', {}, user.full_name);
  const userMeta = h('span', { class: 'text-xs muted' },
    roleLabel(user.role) + (user.branch_name ? ` · ${user.branch_name}` : ''));

  const userRow = h('div', { class: 'sidebar-user-row', tabindex: '0', role: 'button', onclick: () => { closeSidebar(); navigate('/profile'); } },
    h('div', { class: 'avatar', style: `--avatar-bg: ${user.avatar_color || 'var(--c-accent)'}; --avatar-size: 36px; --avatar-font: 13px;` }),
    h('div', { class: 'sidebar-user-info' },
      userName,
      userMeta,
    ),
  );
  footer.appendChild(userRow);

  const logoutBtn = h('button', { class: 'btn btn-ghost btn-sm', onclick: async () => {
    closeSidebar();
    const confirmed = await confirmModal('¿Cerrar sesión?');
    if (!confirmed) return;
    Token.clear();
    CurrentUser.clear();
    navigate('/login');
    toast('Sesión cerrada', 'success');
  }, style: { width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px' } },
    h('span', { html: iconSvg(LogOut, 14) }),
    'Cerrar sesión',
  );
  footer.appendChild(logoutBtn);

  const role = user.role;

  const navItems = [
    { href: '/dashboard', label: 'Panel' },
    ...(role === 'admin' ? [{ href: '/branches', label: 'Sedes' }] : []),
    ...(role === 'admin' ? [{ href: '/audit', label: 'Auditoría' }] : []),
    ...(role === 'admin' || role === 'instructor' ? [{ href: '/services', label: 'Servicios' }] : []),
    ...(role === 'admin' ? [{ href: '/users', label: 'Usuarios' }] : []),
    ...(role === 'admin' ? [{ href: '/registrations', label: 'Solicitudes' }] : []),
    { href: '/sessions', label: 'Sesiones' },
  ];

  clear(nav);
  for (const l of navItems) {
    const icon = ICON_MAP[l.label];
    const a = h('a', { href: 'javascript:void(0)' },
      icon ? h('span', { html: iconSvg(icon, 18) }) : null,
      l.label,
    );
    if (window.location.pathname === l.href) a.classList.add('active');
    a.addEventListener('click', (e) => {
      e.preventDefault();
      closeSidebar();
      navigate(l.href);
    });
    nav.appendChild(a);
  }

  if (toggleBtn) {
    toggleBtn.onclick = () => {
      sidebar.classList.toggle('open');
      if (backdrop) backdrop.classList.toggle('visible');
    };
  }
  if (backdrop) {
    backdrop.onclick = closeSidebar;
  }
};

export const requireAuth = async (allowedRoles = null) => {
  if (!Token.get()) {
    navigate('/login');
    return null;
  }
  let user = CurrentUser.get();
  if (!user) {
    try {
      const me = await AuthAPI.me();
      user = me.data;
      CurrentUser.set(user);
    } catch {
      Token.clear();
      CurrentUser.clear();
      navigate('/login');
      return null;
    }
  }
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    navigate('/dashboard');
    toast('No tiene permiso para acceder a esa página', 'error');
    return null;
  }
  return user;
};
